
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IMemoModel } from "../../common/models/IMemoModel";
import { RouteFromObjectType } from "../../common/utilities/RouteFromObjectType";
import { DurationOptionEnum } from "../../components/custom-duration-dialog/CustomDurationUtils";
import { IOfflinePendingMemo } from "../offline-data-service/Models";
import OfflineDataService from "../offline-data-service/OfflineDataService";
import restService from "../rest-service/RestService";
import { EventDurationEnum, IAlarmHistoryEventModel, IControlHistoryModel, IDataQualityEventModel, IDiagnosticEventModel, IFormEventModel, IObjectHistoryExportOptions, IObjectHistoryModel, IWaterLossEventModel } from "./Models";

interface InternalMemosModel {
    isAdmin: boolean;
    memos: Array<IMemoModel>;
}

interface InternalIObjectHistoryModel {
    alarmHistory: Array<IAlarmHistoryEventModel>;
    controlHistory: Array<IControlHistoryModel>;
    dataQualityEvents: Array<IDataQualityEventModel>;
    diagnosticEvents: Array<IDiagnosticEventModel>;
    memoData: InternalMemosModel;
    formEvents: Array<IFormEventModel>;
    waterLossEvents: Array<IWaterLossEventModel>;
}

type GetHistoryQueryParams = {
    duration: EventDurationEnum,
    beginDate?: Date,
    endDate?: Date
}


const ObjectHistoryDataService = {

    getHistoryData: async function (
        customerID: number,
        groupID: number,
        eLynxObject: IeLynxObject,
        offline: boolean,
        duration?: DurationOptionEnum,
        startDate?: Date,
        endDate?: Date,
        signal?: AbortSignal
    ): Promise<IObjectHistoryModel> {

        if (!offline) {
            const path = `/groups/${groupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/history`;

            switch (eLynxObject.objectType) {
                case eLynxObjectTypeEnum.Device:
                case eLynxObjectTypeEnum.Tank:
                case eLynxObjectTypeEnum.Well:
                case eLynxObjectTypeEnum.TankBattery:
                case eLynxObjectTypeEnum.Tag:
                    const queryParams: GetHistoryQueryParams = {
                        duration: duration === DurationOptionEnum.OneDay
                            ? EventDurationEnum.Last24Hours
                            : duration === DurationOptionEnum.ThreeDays
                                ? EventDurationEnum.Last3Days
                                : duration === DurationOptionEnum.SevenDays
                                    ? EventDurationEnum.Last7Days
                                    : duration === DurationOptionEnum.ThirtyDays
                                        ? EventDurationEnum.Last30Days
                                        : duration === DurationOptionEnum.Custom
                                            ? EventDurationEnum.Custom
                                            : EventDurationEnum.Recent100,
                        beginDate: startDate,
                        endDate: endDate
                    };

                    const response = await restService.get<InternalIObjectHistoryModel, GetHistoryQueryParams>(path, signal, queryParams);

                    return {
                        alarmHistory: response.data.alarmHistory,
                        controlHistory: response.data.controlHistory,
                        dataQualityEvents: response.data.dataQualityEvents,
                        diagnosticEvents: response.data.diagnosticEvents,
                        memos: response.data.memoData.memos,
                        formEvents: response.data.formEvents,
                        waterLossEvents: response.data.waterLossEvents
                    } as IObjectHistoryModel;

                default:
                    throw new Error(`getObjectPropertyData not implemented for ${eLynxObject.objectType}`);
            }
        } else {
            const pendingMemos = await OfflineDataService.getPendingMemos(customerID);

            return {
                alarmHistory: new Array<IAlarmHistoryEventModel>(),
                controlHistory: new Array<IControlHistoryModel>(),
                dataQualityEvents: new Array<IDataQualityEventModel>(),
                diagnosticEvents: new Array<IDiagnosticEventModel>(),
                memos: pendingMemos?.filter(o => o.oid === eLynxObject.id && o.otid === eLynxObject.objectType)
                        .map((memo: IOfflinePendingMemo) => {
                    return {
                        memoID: memo.id,
                        timestamp: new Date(memo.ts),
                        text: memo.txt,
                        memoType: memo.mt,
                        object: {
                            id: memo.oid,
                            objectType: memo.otid
                        },
                        userName: memo.un,
                        userID: memo.uid
                        // Do I need target object?
                    };
                }) ?? new Array<IMemoModel>(),
                formEvents: new Array<IFormEventModel>(),
                waterLossEvents: new Array<IWaterLossEventModel>(),
            } as IObjectHistoryModel;
        }
    },
    exportHistory: async function (groupID: number, 
            eLynxObject: IeLynxObject, 
            exportOptions: IObjectHistoryExportOptions): Promise<void> {

        const path = `groups/${groupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/history/export`;
    
        await restService.post<IObjectHistoryExportOptions, void>(path, exportOptions);
    }
};

export default ObjectHistoryDataService;